/**
 * Columnar output types — the decoded shape produced by the transport layer.
 *
 * Transport is rendering-agnostic: it never imports `@qufolio/chart-engine`
 * and never holds SciChart references. Instead it decodes QV Arrow bundles into
 * plain columnar arrays keyed by indicator alias, which the chart engine (or any
 * other consumer) maps trivially onto its own `SeriesDelta` (x[] + per-alias y[]).
 */

/**
 * Standard OHLCV column names QV emits on the base price frame alongside the
 * per-indicator alias columns. Used to recognise candle inputs.
 */
export const OHLCV_COLUMNS = ["open", "high", "low", "close", "volume"] as const;

/** Column names that, when numeric, are treated as the time / x axis. */
export const TIME_COLUMN_NAMES = ["date", "time", "timestamp", "x", "index"] as const;

/** Non-numeric scalar columns QV carries for identity (single value per group). */
export const LABEL_COLUMN_NAMES = ["symbol", "sector"] as const;

/**
 * How the {@link ChartGroupColumns.x} axis should be interpreted.
 * - `seconds` — unix epoch seconds (decoded from a Timestamp/Date column)
 * - `index`   — a synthetic 0..n row index (no time column was present)
 */
import type { GeometryPayload } from "./geometry.js";

export type XAxisKind = "seconds" | "index";

/**
 * One measured span, server-side or client-side. `ms` is wall clock.
 *
 * Named the same on both sides so the `i` panel renders one list rather than
 * two shapes that happen to look alike.
 */
export interface PerfStage {
  name: string;
  ms: number;
  /** What the stage turned out to have done — "7 aliases", "1d — identity". */
  detail?: string;
}

/** Which ask a frame answers; see {@link ChartGroupColumns.answers}. */
export interface AskStamp {
  symbol?: string;
  frequency?: string;
  /** Sorted and de-duplicated, as the server's channel key spells them. */
  aliases?: string[];
}

/**
 * One symbol's read, hoisted out of the server's per-group loop.
 *
 * Separate from a group's stages because the server reads each distinct symbol
 * **once** and hands the frame to every group that wants it. Two charts on one
 * symbol pay for it together, so attributing it to one of them would be a lie
 * about both.
 */
export interface PerfSharedRead {
  symbol: string;
  readMs: number;
  /** Absent when nothing was spliced — a settled market, or no freshness. */
  realtimeMs?: number;
  /** The Redis **key** the session bar came from. Never a URL, never a token. */
  rtKey?: string;
  /** Whether that snapshot was served from the poller's cache. */
  rtWarm?: boolean;
  rtDigest?: string;
  phase?: string;
  /** The exception class, when the read failed. The message is the group's. */
  error?: string;
}

/**
 * What the server says it spent building this response.
 *
 * Decoded from the bundle's optional trailing block. `undefined` means the
 * server sent none — an older build, or one that could not measure — which is
 * a different statement from "it took no time" and is rendered as such.
 */
export interface ServerPerf {
  v: number;
  /**
   * `refresh` marks a rebuild the server did **once for the channel** and
   * pushed to every subscriber. Reading those timings as your own latency is
   * the mistake this field exists to prevent.
   */
  source: "request" | "refresh";
  host?: string;
  machine?: string;
  phase?: string;
  builtAtMs: number;
  /**
   * **This chart group's own bracket** — the read it waited for, plus its build.
   * Deliberately not the sum of its stages: the panel prints the difference as
   * unattributed, which is where a stage nobody wrapped shows up.
   */
  totalMs: number;
  /**
   * The whole response, every group and every shared read.
   *
   * Kept apart from {@link totalMs} because charging one chart with the
   * bundle's total is a real and large error: on a layout of AAPL and MSFT
   * where AAPL's snapshot read took three seconds, MSFT's panel would report
   * three seconds of unattributed time it never spent.
   */
  bundleTotalMs: number;
  /**
   * What the request waited through **inside the server, before the build
   * began** — the socket's sign-in on its first request, then the wait for a
   * worker thread. Paid once per request, not per chart, and outside
   * {@link bundleTotalMs}.
   *
   * Empty from a server that does not report it, and on a refresh. Without it
   * the panel billed this wait to the wire: 3.1 s of "wire" on 2026-09-25 was a
   * sign-in check against a slow Postgres, with ~0.1 s of actual transport.
   */
  beforeBuild?: PerfStage[];
  shared: PerfSharedRead[];
  stages: PerfStage[];
  symbol?: string;
  frequency?: string;
  barSource?: string;
  barsRequested?: number;
  barsReturned?: number;
  bytes?: number;
}

/**
 * One chart group's decoded columns. Maps trivially to a set of `SeriesDelta`:
 * every entry of {@link columns} is one alias's y[] parallel to the shared {@link x}.
 */
export interface ChartGroupColumns {
  /** The `ChartGroupId` from the bundle header, stringified. */
  chartGroupId: string;

  /** `ok` for a decoded Arrow table; `error` when the server reported a failure. */
  status: "ok" | "error";

  /** Shared x axis for every column, `x.length === columns[alias].length`. */
  x: number[];

  /** Interpretation of {@link x}. */
  xKind: XAxisKind;

  /** Per-alias numeric columns (indicator outputs + OHLCV), keyed by column name. */
  columns: Record<string, number[]>;

  /** Scalar string columns (e.g. `symbol`, `sector`) — one value each. */
  labels: Record<string, string>;

  /** Convenience mirror of `labels.symbol`, when present. */
  symbol?: string;

  /** Populated only when `status === "error"`: the server's UTF-8 error string. */
  error?: string;

  /**
   * This is a refresh's last few bars, not a load — the table's Arrow schema
   * says `qv.frame = tail`.
   *
   * A tail is only ever diffed against what is held. It is never drawn as a
   * load: a refresh landing just after a symbol change, when the snapshot has
   * been dropped for the answer on its way, would otherwise be taken for that
   * answer and drawn as two bars of chart. Absent means a whole load, which is
   * also what an old server sends.
   */
  tail?: boolean;

  /**
   * The ask this table answers — `qv.symbol`, `qv.frequency` and `qv.aliases`
   * on the schema, stamped by the server on every load and every tail.
   *
   * Not the `symbol` label: that is a column of the data, and this says which
   * *request* the frame is for. Two requests for one `chartGroupId` in flight —
   * a click on JPM, then on META, inside one round trip — answered in order, so
   * JPM's whole load reached the rebuilt META surface as its bulk load and
   * META's own answer was then diffed onto it as one patched bar. An interval
   * or an indicator changed as fast is the same race. Each part is absent on
   * an old server, and then that part is not checked.
   */
  answers?: AskStamp;

  /**
   * This load is every bar the symbol has — the schema says `qv.complete = 1`.
   * Daily since 2026-09-18. A span reaching past the first bar is then clamped
   * to it rather than refetched: there is nothing further back to fetch.
   */
  complete?: boolean;

  /**
   * Where an intraday chart's **today** came from — `intra` (qf-intra-calc-us,
   * the live feed) or `rest` (the fallback) — from `qv.today` on the schema.
   * Absent on daily and coarser charts, and from an older server.
   */
  todaySource?: "intra" | "rest";

  /**
   * Whether bars are being made right now — `live` (a trading day, 04:00-20:05
   * ET) or `closed` — from `qv.phase`. Absent where `todaySource` is.
   */
  phase?: "live" | "closed";

  /**
   * The last extended-hours price — pre-market before 09:30, post-market after
   * 16:00 ET — from `qv.ext`, with which one in `extPhase` (`qv.extPhase`).
   * Absent during the session and when nothing printed.
   */
  extPrice?: number;
  extPhase?: "pre" | "post";

  /**
   * Shapes the server's detectors found — trendlines, later chart patterns —
   * from `qf.geometry` on the schema. Keyed by plot alias. Absent when no
   * pattern plot asked, or the payload could not be read. See `geometry.ts`.
   */
  geometry?: GeometryPayload;

  /**
   * What the server spent building this group, when it said.
   *
   * The bundle-level facts are folded in beside this group's own stages, so a
   * consumer holding one group holds a complete reading and never has to carry
   * the bundle around to interpret it.
   */
  serverPerf?: ServerPerf;
}

/** A full decoded bundle: one layout, many chart groups. */
export interface ColumnarBundle {
  /** The `LayoutId` echoed back in the bundle header (trimmed). */
  layoutId: string;

  /** Decoded groups, in the order the server packed them. */
  groups: ChartGroupColumns[];
}

/**
 * Incremental delta for one alias — structurally identical to chart-engine's
 * `SeriesDelta`, so `delta as SeriesDelta` is a safe, dependency-free cast.
 * Either appends new points or patches the last point (or both).
 */
export interface ColumnDelta {
  /** Which output alias / column this delta applies to. */
  alias: string;

  /** New points to append (columnar: parallel arrays). */
  append?: {
    x: number[];
    y: number[];
    open?: number[];
    high?: number[];
    low?: number[];
    close?: number[];
  };

  /** Patch the last existing point in place. */
  patchLast?: {
    y?: number;
    open?: number;
    high?: number;
    low?: number;
    close?: number;
  };
}

/** True when the group carries a full OHLC set (a candle can be assembled). */
export function hasOhlc(group: ChartGroupColumns): boolean {
  return (
    Array.isArray(group.columns["open"]) &&
    Array.isArray(group.columns["high"]) &&
    Array.isArray(group.columns["low"]) &&
    Array.isArray(group.columns["close"])
  );
}

/**
 * Build a candle {@link ColumnDelta} from a group's OHLC columns. Returns
 * `undefined` when the group lacks a full OHLC set. `y` mirrors `close` so a
 * line consumer of the same alias still renders sensibly.
 *
 * @param group - The decoded chart group.
 * @param alias - The alias the candle series is registered under.
 */
export function candleDelta(
  group: ChartGroupColumns,
  alias: string,
): ColumnDelta | undefined {
  if (!hasOhlc(group)) return undefined;
  const open = group.columns["open"]!;
  const high = group.columns["high"]!;
  const low = group.columns["low"]!;
  const close = group.columns["close"]!;
  return {
    alias,
    append: { x: group.x.slice(), y: close.slice(), open, high, low, close },
  };
}

/**
 * Map a decoded group to one bulk line {@link ColumnDelta} per column.
 * Each column becomes an `append` of the full series against the shared x.
 * This is the trivial columnar → SeriesDelta bridge for a first (bulk) load.
 *
 * @param group   - The decoded chart group.
 * @param aliases - Optional subset of column names; defaults to every column.
 */
export function columnarToDeltas(
  group: ChartGroupColumns,
  aliases?: readonly string[],
): ColumnDelta[] {
  const keys = aliases ?? Object.keys(group.columns);
  const deltas: ColumnDelta[] = [];
  for (const alias of keys) {
    const y = group.columns[alias];
    if (!y) continue;
    deltas.push({ alias, append: { x: group.x.slice(), y: y.slice() } });
  }
  return deltas;
}

/**
 * The last row that carries a real bar — the frame's **settled edge**.
 *
 * A frame may run past its last bar. A seasonal projection is drawn on dates
 * the symbol has no bar for, so the server sends those rows with every OHLCV
 * column null, and `close` being finite is exactly what tells a bar from a
 * forecast. Derived rather than announced: the alternative was a count in the
 * envelope, and a second statement of a fact the data already carries is a
 * second thing that can be wrong.
 *
 * Falls back to the last row when there is no `close` at all, which keeps a
 * frame carrying only indicator columns behaving as it always did.
 */
function settledIndex(group: ChartGroupColumns): number {
  const close = group.columns["close"];
  if (!close) return group.x.length - 1;
  for (let i = close.length - 1; i >= 0; i--) {
    if (Number.isFinite(close[i])) return i;
  }
  return -1;
}

/**
 * The x of the newest real bar in a snapshot — not of its last row, which with
 * a projection loaded is a date months out. `undefined` when it holds no bar.
 */
export function settledX(group: ChartGroupColumns): number | undefined {
  const at = settledIndex(group);
  return at >= 0 ? group.x[at] : undefined;
}

/**
 * Diff two snapshots of the same chart group into append / patch-last deltas.
 *
 * QV recomputes and re-sends a whole bundle per frame; realtime is expressed by
 * diffing consecutive snapshots. Rows whose x is greater than the previous last
 * x are appended; a row re-sent at the previous last x with a changed value is a
 * patch-last (the in-progress bar).
 *
 * **Measured from the settled edge, not from the last row, and the difference
 * is a chart that silently stops.** Once a frame can carry projected rows, its
 * last x is a date in the future and no row in the *next* frame is ever greater
 * than it — so nothing appends, the patch lands on the far end of a forecast,
 * and the price stops updating on the 60-second refresh. No error, no badge,
 * and a green socket: the only symptom is that a liquid symbol has gone quiet,
 * which is indistinguishable from a quiet market.
 *
 * Projected rows are skipped entirely rather than diffed. They are recomputed
 * whole on every load — the anchor moves with the price — so there is no
 * append or patch that expresses the change, and re-sending them each frame
 * would append the same forecast on top of itself. They arrive with the bulk
 * load and stay until the chart rebuilds.
 *
 * @param prev - The previous snapshot (already emitted).
 * @param next - The freshly decoded snapshot.
 */
export function diffChartGroup(
  prev: ChartGroupColumns,
  next: ChartGroupColumns,
): ColumnDelta[] {
  const settledPrev = settledIndex(prev);
  const settledNext = settledIndex(next);
  const lastPrevX = settledPrev >= 0 ? prev.x[settledPrev] : undefined;

  const appendIdx: number[] = [];
  let patchIdx = -1;
  for (let i = 0; i <= settledNext; i++) {
    const x = next.x[i]!;
    if (lastPrevX === undefined || x > lastPrevX) appendIdx.push(i);
    else if (x === lastPrevX) patchIdx = i;
  }

  const deltas: ColumnDelta[] = [];
  for (const alias of Object.keys(next.columns)) {
    const col = next.columns[alias];
    if (!col) continue;

    const delta: ColumnDelta = { alias };

    if (appendIdx.length > 0) {
      delta.append = {
        x: appendIdx.map((i) => next.x[i]!),
        y: appendIdx.map((i) => col[i]!),
      };
    }

    if (patchIdx >= 0) {
      const prevCol = prev.columns[alias];
      const nextVal = col[patchIdx]!;
      // Read at the **settled edge**, not at the end of the array. The two are
      // the same row only when nothing is projected; with a forecast on the
      // chart the last element is its far end, so every column would compare
      // today's bar against a value months away, find them different, and emit
      // a patch on every refresh — writing a forecast's tail onto the live bar.
      const prevVal =
        prevCol && settledPrev >= 0 && settledPrev < prevCol.length
          ? prevCol[settledPrev]
          : undefined;
      if (prevVal !== nextVal) delta.patchLast = { y: nextVal };
    }

    if (delta.append || delta.patchLast) deltas.push(delta);
  }
  return deltas;
}